/**
 * PD-16475 — rich text on issues (description, notes, portal messages).
 *
 * Issue text is written by CRM staff AND by customers on the storefront, and
 * shown to both, so the stored HTML is a security boundary. This is an
 * ALLOW-list rebuilt from a token stream, the same approach as the website
 * sanitizer (`sanitizeRichtext`): the input is never patched, the output is
 * re-emitted from scratch using only the tags below, every attribute except a
 * vetted link `href` is dropped, and all text is re-escaped.
 *
 * Every rich value is stored next to a plain-text copy (`issue.description`,
 * `issue_note.note`) derived by `issueRichTextToPlainText`. Everything that
 * reads the text rather than displaying it — the legacy CRM, the issue email,
 * search, AI tools, list columns — keeps reading the plain column unchanged.
 *
 * htmlparser2 rather than DOMPurify because this runs on the server and in
 * the browser; DOMPurify needs a DOM.
 *
 * The same allow-list also holds the terms a form's consent checkbox shows
 * (PD-16691), which staff write and anonymous visitors read, so a change to
 * what it keeps changes what those terms can contain too.
 */
import { Parser } from 'htmlparser2';

import { isAllowedHref } from '../website-schema/richtext-sanitizer';

/** Elements whose entire content is dropped, not just the tags. */
const DROPPED_WITH_CONTENT: ReadonlySet<string> = new Set([
	'script',
	'style',
	'iframe',
	'object',
	'embed',
	'svg',
	'math',
	'template',
	'noscript',
	'head',
	'title',
	'textarea',
	'select',
	'form',
	'canvas',
	'audio',
	'video',
]);

/** Source tag → the tag written out. Pasted `<b>`/`<i>` become their semantic forms. */
const TAG_ALIASES: Readonly<Record<string, string>> = { b: 'strong', i: 'em' };

/** What the issue editor can produce: bold, italic, underline, lists, links. */
const ALLOWED_TAGS: ReadonlySet<string> = new Set([
	'p',
	'br',
	'strong',
	'em',
	'u',
	'ul',
	'ol',
	'li',
	'a',
]);

const VOID_TAGS: ReadonlySet<string> = new Set(['br']);

/** Tags that end a line in the plain-text copy. */
const BLOCK_TAGS: ReadonlySet<string> = new Set(['p', 'li', 'ul', 'ol', 'div']);

/** Runs on sanitized output only, where a paragraph tag never carries an attribute. */
const TRAILING_EMPTY_PARAGRAPHS = /(?:<p>(?:<br>)?<\/p>)+$/;

function escapeText(text: string): string {
	return text
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;');
}

function escapeAttribute(text: string): string {
	return escapeText(text).replace(/"/g, '&quot;');
}

/**
 * Reduce HTML to what an issue may store. Idempotent. Returns '' for input
 * with no visible text, so callers can treat "only formatting" as empty.
 */
export function sanitizeIssueRichText(html: string): string {
	const output: Array<string> = [];
	const openTags: Array<string> = [];
	let droppedDepth = 0;

	const parser = new Parser(
		{
			onopentag(rawName, attributes): void {
				const name = rawName.toLowerCase();

				if (droppedDepth > 0 || DROPPED_WITH_CONTENT.has(name)) {
					if (!VOID_TAGS.has(name)) {
						droppedDepth++;
					}
					return;
				}

				const tag = TAG_ALIASES[name] ?? name;

				if (!ALLOWED_TAGS.has(tag)) {
					return;
				}

				if (VOID_TAGS.has(tag)) {
					output.push(`<${tag}>`);
					return;
				}

				if (tag === 'a') {
					const href = attributes['href'];

					// A link with no acceptable destination keeps its text but loses the tag.
					if (href == null || !isAllowedHref(href, { isHttpAllowed: true })) {
						openTags.push('');
						return;
					}

					const isExternal = /^https?:/i.test(href);
					output.push(
						`<a href="${escapeAttribute(href)}"${isExternal ? ' target="_blank" rel="noopener noreferrer"' : ''}>`,
					);
					openTags.push('a');
					return;
				}

				output.push(`<${tag}>`);
				openTags.push(tag);
			},
			ontext(text): void {
				if (droppedDepth === 0) {
					output.push(escapeText(text));
				}
			},
			onclosetag(rawName): void {
				const name = rawName.toLowerCase();

				if (droppedDepth > 0) {
					if (!VOID_TAGS.has(name)) {
						droppedDepth--;
					}
					return;
				}

				const tag = TAG_ALIASES[name] ?? name;

				if (!ALLOWED_TAGS.has(tag) || VOID_TAGS.has(tag)) {
					return;
				}

				const opened = openTags.pop();

				if (opened != null && opened.length > 0) {
					output.push(`</${opened}>`);
				}
			},
		},
		{ decodeEntities: true, lowerCaseTags: true, recognizeSelfClosing: true },
	);

	parser.write(html);
	parser.end();

	// Unclosed tags (a truncated paste) are closed so the stored value is
	// always well-formed.
	while (openTags.length > 0) {
		const opened = openTags.pop();

		if (opened != null && opened.length > 0) {
			output.push(`</${opened}>`);
		}
	}

	const sanitized = output.join('').replace(TRAILING_EMPTY_PARAGRAPHS, '');

	return issueRichTextToPlainText(sanitized).length === 0 ? '' : sanitized;
}

/**
 * The plain-text copy of an issue's rich text: paragraphs and list items on
 * their own lines, bulleted and numbered lists marked, links written as
 * "text (url)" when the text is not the url itself, whitespace collapsed.
 */
export function issueRichTextToPlainText(html: string): string {
	const lines: Array<string> = [];
	let current = '';
	const lists: Array<{ ordered: boolean; index: number }> = [];
	const links: Array<{ href: string; textStart: number }> = [];
	let droppedDepth = 0;
	// The list marker ("- ", "1. ") written for the item just opened, until
	// the item's text arrives. The editor wraps each item's text in a <p>, so
	// that paragraph must not push the marker onto a line of its own.
	let pendingMarker: string | null = null;
	// Whitespace between <li> and its <p> (pasted, indented markup) does not
	// count as the item's text.
	const holdsOnlyMarker = (): boolean =>
		pendingMarker != null && current.trim() === pendingMarker.trim();

	const endLine = (): void => {
		const trimmed = current.replace(/[ \t]+/g, ' ').trim();

		// A marker with no text after it is an empty list item; drop it.
		if (trimmed.length > 0 && !holdsOnlyMarker()) {
			lines.push(trimmed);
		}

		current = '';
		pendingMarker = null;
	};

	const parser = new Parser(
		{
			onopentag(rawName, attributes): void {
				const name = rawName.toLowerCase();

				if (droppedDepth > 0 || DROPPED_WITH_CONTENT.has(name)) {
					if (name !== 'br') {
						droppedDepth++;
					}
					return;
				}

				if (name === 'br') {
					endLine();
					return;
				}

				if (BLOCK_TAGS.has(name) && !holdsOnlyMarker()) {
					endLine();
				}

				if (name === 'ul' || name === 'ol') {
					lists.push({ ordered: name === 'ol', index: 0 });
					return;
				}

				if (name === 'li') {
					const list = lists.at(-1);

					if (list != null) {
						list.index++;
						current = list.ordered ? `${list.index}. ` : '- ';
						pendingMarker = current;
					}
					return;
				}

				if (name === 'a') {
					links.push({
						href: attributes['href'] ?? '',
						textStart: current.length,
					});
				}
			},
			ontext(text): void {
				if (droppedDepth === 0) {
					current += text.replace(/\s+/g, ' ');
				}
			},
			onclosetag(rawName): void {
				const name = rawName.toLowerCase();

				if (droppedDepth > 0) {
					if (name !== 'br') {
						droppedDepth--;
					}
					return;
				}

				if (name === 'a') {
					const link = links.pop();

					if (link != null && link.href.length > 0) {
						const text = current.slice(link.textStart).trim();

						if (text.length > 0 && text !== link.href) {
							current += ` (${link.href})`;
						}
					}
					return;
				}

				if (name === 'ul' || name === 'ol') {
					endLine();
					lists.pop();
					return;
				}

				if (BLOCK_TAGS.has(name)) {
					endLine();
				}
			},
		},
		{ decodeEntities: true, lowerCaseTags: true, recognizeSelfClosing: true },
	);

	parser.write(html);
	parser.end();
	endLine();

	return lines.join('\n');
}

/**
 * Rich text for a value that was stored as plain text (every issue written
 * before rich text existed, and anything written through a plain-text path
 * such as an AI tool): escaped, one paragraph per line.
 */
export function plainTextToIssueRichText(text: string): string {
	return text
		.split(/\r?\n/)
		.map((line) => line.trim())
		.filter((line) => line.length > 0)
		.map((line) => `<p>${escapeText(line)}</p>`)
		.join('');
}
